import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { monthsDiff, parseAgeToMonths } from '../../../utils/DateTimeFormat';
import {
  ICustomerBooking,
  OrderVaccineRequestObject,
  defaultOrderVaccineRequestObject,
} from '../../../api/models/common';

export interface IVaccineFormValues {
  orderType: string;
  note?: string;
  testDay: string;
  testHour: string;
  infomation?: ICustomerBooking;
  isContrastAgents?: boolean;
  checkCategory?: boolean;
  vaccineId?: number;
  scheduleId?: number;
  doseId?: number;
  previousPhoneNumber?: string;
  orderAddress: string;
  contactPhone: string;
  vaccineOrdered?: OrderVaccine[];
  vaccineBrand?: string;
  currentIllness?: string;
}
export interface IVaccineFormEditValues {
  id?: number;
  infoBooking?: ICustomerBooking;
  orderType: string;
  note?: string;
  currentIllness?: string;
  vaccineBrand?: string;
  testDay: string;
  testHour: string;
  infomation?: ICustomerBooking;
  isContrastAgents?: boolean;
  vaccineId?: number;
  scheduleId?: number;
  doseId?: number;
}
export interface IVaccineBookingSuccess {
  bookingUser: {
    doctor?: boolean;
    fullName: string;
    phone: string;
    callingCodes: string;
    nameCountry: string;
    userId: number;
  };
  deleted?: boolean;
  status: string;
  dateTest: string;
  testDate: string;
  dateTestExpired: string;
  id: number;
  note?: string;
  category: {
    id: number;
    name: string;
    place?: string;
  };
  contrast?: boolean;
  orderType: string;
  infomation: {
    id: number;
    dateOfBirthday: string;
    email: string;
    fullName: string;
    gender: string;
    phone: string;
    callingCodes: string;
    nameCountry: string;
    address: string;
  };
  type: string;
  orderAddress: string;
  vaccineDose: {
    id: number;
    name: string;
    ages: string;
    schedule: ScheduleBase;
    key: {
      id: number;
      description: string;
      color: string;
      ageGroup: AgeGroup;
    };
  };

  vaccine: {
    id: number;
    orderIndex: number;
    name: string;
  };
  vaccineBrand?: string;
  currentIllness?: string;
  orderSecureCode?: string;
}

export interface DataPropsVaccine {
  inforBookingForSelf: IVaccineFormValues[];
  inforBookingForOther: IVaccineFormValues[];
  isFamily?: boolean;
}

export interface DoseKey {
  id: number;
  description: string;
  color: string;
  ageGroup: AgeGroup;
}

// Forward reference interfaces to break circular dependency
export interface Vaccine {
  id: number;
  orderIndex: number;
  name: string;
  schedules: Schedule[];
}

// Define base interfaces without circular references
export interface VaccineBase {
  id: number;
  orderIndex: number;
  name: string;
  diseaseCausedBy: string;
}

export interface ScheduleBase {
  id: number;
  orderIndex: number;
  vaccine: VaccineBase;
  diseaseComplications: string;
  preventableDisease: string;
  ageGroup: AgeGroup;
}

export interface DoseBase {
  id: number;
  name: string;
  ages: string;
  key: DoseKey;
  doseIndex: number;
  viewPosition: string;
  schedule?: ScheduleBase;
}

export interface OrderVaccineBase {
  id: number;
  name: string;
  status: string;
  note: string;
}
export interface OrderVaccine extends OrderVaccineBase {
  vaccine: VaccineBase;
  vaccineDose: DoseBase;
  testDate: string;
  adverseEvent: string;
  vaccineBrand: string;
  currentIllness: string;
  vaccinationCenter: string;
  plannedDate: string;
  vaccineNote: string;
  addNewVaccineOrder: boolean;
  canModify: boolean;
}

export interface OrderVaccineWithInfomation {
  orders: OrderVaccine[];
  infomation: ICustomerBooking;
}

export interface VaccineOrderUpdateRequest {
  id: number;
  orderId?: number;
  testDayTime: string;
  vaccineDoseId: number;
  adverseEvent: string;
  vaccineBrand: string;
  currentIllness: string;
  vaccinationCenter: string;
  plannedDate: string;
  vaccineNote: string;
  doctorId?: number;
}

// Now define the full interfaces with relationships
export interface Schedule extends ScheduleBase {
  doses: Dose[];
}

export interface Dose extends DoseBase {
  schedule?: ScheduleBase; // Reference the base type to avoid circular dependency
  order: OrderVaccine;
}

// Use the same structure as Schedule to maintain consistency
export type VaccineSchedule = Schedule;

export interface VaccineData {
  INFANT: VaccineSchedule[];
  CHILD: VaccineSchedule[];
  ADULT: VaccineSchedule[];
}
export type AgeGroup = keyof VaccineData;

export interface VaccineKey {
  id: number;
  description: string;
  color: string;
  ageGroup: AgeGroup;
}
export interface VaccineKeyData {
  INFANT: VaccineKey[];
  CHILD: VaccineKey[];
  ADULT: VaccineKey[];
}

export const vaccineTitleColor = {
  MISSING: 'column-label__missing-dose',
  SUCCESS: 'column-label__success',
  WARNING: 'column-label__warning',
  DEFAULT: 'column-label__base',
};

export interface VaccineState {
  pastDoses?: Record<string, Dose[]>; // Optional field to store past doses
  vaccineOrdersGrouped?: Record<string, OrderVaccine[]>;
  vaccineDosesGrouped?: Record<string, Dose[]>;
  agesRangeSelected?: AgeGroup;
  infoBooking: DataPropsVaccine;
  vaccineKeyData?: VaccineKeyData;
  infoBookingsuccessList?: IVaccineBookingSuccess[];
  vaccineData?: VaccineData;
  vaccineRender?: VaccineData;
  vaccines?: Vaccine[];
  doseRequest?: IVaccineFormEditValues;
  currentVaccineOrderData?: OrderVaccine;
  orderVaccines?: OrderVaccine[];
  vaccineEdit?: IVaccineFormEditValues;
  vaccineOrdersUserInfoMap?: Record<number, number[]>;
  userInfoVaccineOrderMap?: Record<number, OrderVaccine[]>;
  // UI state for History Vaccine
  vaccineHistoryUI?: {
    selectedSchedule: Schedule | null;
    showModalVaccinedOrders: boolean;
    selectedVaccine: VaccineBase | null;
    showAddForm: boolean;
    vaccineOrderRequest: OrderVaccineRequestObject;
    vaccineModifying: VaccineOrderUpdateRequest | null;
  };
}

const initialState: VaccineState = {
  vaccineOrdersGrouped: {},
  agesRangeSelected: 'INFANT',
  vaccineKeyData: {
    INFANT: [],
    CHILD: [],
    ADULT: [],
  },
  infoBooking: {
    isFamily: false,
    inforBookingForOther: [],
    inforBookingForSelf: [],
  },
  infoBookingsuccessList: [],
  vaccines: [],
  currentVaccineOrderData: undefined,
  vaccineHistoryUI: {
    selectedSchedule: null,
    showModalVaccinedOrders: false,
    selectedVaccine: null,
    showAddForm: false,
    vaccineOrderRequest: defaultOrderVaccineRequestObject,
    vaccineModifying: null,
  },
};

export const vaccineSlice = createSlice({
  name: 'vaccine',
  initialState,
  reducers: {
    setReset: state => {
      state.infoBooking = initialState.infoBooking;
      state.vaccineData = initialState.vaccineData;
      state.vaccineKeyData = initialState.vaccineKeyData;
      state.infoBookingsuccessList = initialState.infoBookingsuccessList;
      state.doseRequest = initialState.doseRequest;
    },
    changeAgesRangeSelected: (state, action: PayloadAction<AgeGroup>) => {
      state.agesRangeSelected = action.payload;
    },
    loadVaccineOrdersUserInfoMap: (
      state,
      action: PayloadAction<{ infoId: number; doseOrderedIds: number[] }>
    ) => {
      const { infoId, doseOrderedIds } = action.payload;
      if (!state.vaccineOrdersUserInfoMap) {
        state.vaccineOrdersUserInfoMap = {};
      }
      // Kiểm tra nếu infoId đã tồn tại trong map
      if (!state.vaccineOrdersUserInfoMap[infoId]) {
        state.vaccineOrdersUserInfoMap[infoId] = [];
      }
      // Thêm các doseOrderedIds vào mảng tương ứng với infoId
      doseOrderedIds.forEach(doseId => {
        state.vaccineOrdersUserInfoMap?.[infoId].push(doseId);
      });
    },
    loadUserInfoVaccineOrderMap: (
      state,
      action: PayloadAction<{ infoId: number; vaccineOrdereds: OrderVaccine[] }>
    ) => {
      const { infoId, vaccineOrdereds } = action.payload;
      if (!state.userInfoVaccineOrderMap) {
        state.userInfoVaccineOrderMap = {};
      }
      // Initialize array for this infoId if missing
      if (!state.userInfoVaccineOrderMap[infoId]) {
        state.userInfoVaccineOrderMap[infoId] = [];
      }
      // Append orders for this infoId
      state.userInfoVaccineOrderMap[infoId].push(...vaccineOrdereds);
    },
    loadOrderVaccineToVaccineRender: (state, action: PayloadAction<OrderVaccine[]>) => {
      if (
        !state.vaccineData ||
        !state.vaccineData.INFANT ||
        !state.vaccineData.CHILD ||
        !state.vaccineData.ADULT
      ) {
        return;
      }
      const vaccines = JSON.parse(JSON.stringify(state.vaccineData));
      const vaccineRenderUpdate = insertOrdersIntoVaccineData(action.payload, vaccines);
      state.vaccineRender = vaccineRenderUpdate;
      state.orderVaccines = action.payload;
    },
    loadCurrentVaccineOrderData: (state, action) => {
      state.currentVaccineOrderData = action.payload;
    },
    setInfoBookinguccessList: (state, action) => {
      state.infoBookingsuccessList = action.payload;
    },
    setInfoBookings: (state, action) => {
      state.infoBooking = action.payload;
    },

    loadVaccines: (state, action: PayloadAction<Vaccine[]>) => {
      state.vaccines = action.payload;
    },
    loadVaccineOrdersGrouped: (
      state,
      action: PayloadAction<{
        vaccines: VaccineData | never[];
        vaccineOrders: OrderVaccine[];
        userInfo: ICustomerBooking;
      }>
    ) => {
      // nếu vaccines không tồn tại thì không làm gì cả
      if (!action.payload.vaccines || !action.payload.vaccineOrders || !action.payload.userInfo) {
        return;
      }
      const { vaccineOrders, userInfo, vaccines } = action.payload;

      // Check if vaccines is VaccineData and not never[]
      if (
        Array.isArray(vaccines) ||
        !('INFANT' in vaccines) ||
        !('CHILD' in vaccines) ||
        !('ADULT' in vaccines)
      ) {
        return;
      }

      state.vaccineOrdersGrouped = {};
      state.pastDoses = {};
      state.vaccineDosesGrouped = {};

      const dateOfBirth = userInfo?.dateOfBirthday || '';

      const diffMonths = monthsDiff(dateOfBirth);
      // tạo map {vacciname : orderVaccine}
      const vaccineOrderMap: Record<string, OrderVaccine[]> = {};

      // lấy vaccine từ vaccines
      vaccines.INFANT.forEach(schedule => {
        const key = `${schedule.vaccine.name}`;
        vaccineOrderMap[key] = [];
        schedule.doses.forEach(dose => {
          if (!state.vaccineDosesGrouped) {
            state.vaccineDosesGrouped = {};
          }
          if (!state.vaccineDosesGrouped[key]) {
            state.vaccineDosesGrouped[key] = [];
          }
          state.vaccineDosesGrouped[key].push(dose);

          if (!state.pastDoses) {
            state.pastDoses = {};
          }
          if (!state.pastDoses[key]) {
            state.pastDoses[key] = [];
          }
          const [start, end] = parseAgeToMonths(dose.ages);
          if (+diffMonths >= start) {
            state.pastDoses[key].push(dose);
          }
        });
      });
      vaccines.ADULT.forEach(schedule => {
        const key = `${schedule.vaccine.name}`;
        vaccineOrderMap[key] = [];
        schedule.doses.forEach(dose => {
          if (!state.vaccineDosesGrouped) {
            state.vaccineDosesGrouped = {};
          }
          if (!state.vaccineDosesGrouped[key]) {
            state.vaccineDosesGrouped[key] = [];
          }
          state.vaccineDosesGrouped[key].push(dose);

          if (!state.pastDoses) {
            state.pastDoses = {};
          }
          if (!state.pastDoses[key]) {
            state.pastDoses[key] = [];
          }
          const [start, end] = parseAgeToMonths(dose.ages);
          if (+diffMonths >= start) {
            state.pastDoses[key].push(dose);
          }
        });
      });
      vaccines.CHILD.forEach(schedule => {
        const key = `${schedule.vaccine.name}`;
        vaccineOrderMap[key] = [];
        schedule.doses.forEach(dose => {
          if (!state.vaccineDosesGrouped) {
            state.vaccineDosesGrouped = {};
          }
          if (!state.vaccineDosesGrouped[key]) {
            state.vaccineDosesGrouped[key] = [];
          }
          state.vaccineDosesGrouped[key].push(dose);

          if (!state.pastDoses) {
            state.pastDoses = {};
          }
          if (!state.pastDoses[key]) {
            state.pastDoses[key] = [];
          }
          const [start, end] = parseAgeToMonths(dose.ages);
          if (+diffMonths >= start) {
            state.pastDoses[key].push(dose);
          }
        });
      });

      vaccineOrders.forEach(order => {
        const key = `${order.vaccine.name}`;
        if (vaccineOrderMap[key]) {
          const [start, end] = parseAgeToMonths(order.vaccineDose.ages);
          if (+diffMonths < start) {
            return; // Skip if the age does not match
          }
          vaccineOrderMap[key].push(order);
        }
      });
      state.vaccineOrdersGrouped = vaccineOrderMap;

      // Duyệt qua vaccineOrderMap để cập nhật màu sắc
    },
    loadVaccineData: (state, action) => {
      const { INFANT, CHILD, ADULT } = action.payload;
      if (!state.vaccineData) {
        state.vaccineData = {
          INFANT: [],
          CHILD: [],
          ADULT: [],
        };
      }
      state.vaccineData.INFANT = INFANT;
      state.vaccineData.CHILD = CHILD;
      state.vaccineData.ADULT = ADULT;
      // Cập nhật vaccineRender để hiển thị dữ liệu mới
      state.vaccineRender = {
        INFANT: INFANT,
        CHILD: CHILD,
        ADULT: ADULT,
      };
    },
    loadVaccineKeyData: (state, action: PayloadAction<VaccineKeyData>) => {
      const { INFANT, CHILD, ADULT } = action.payload;
      if (!state.vaccineKeyData) {
        state.vaccineKeyData = {
          INFANT: [],
          CHILD: [],
          ADULT: [],
        };
      }
      state.vaccineKeyData.INFANT = INFANT;
      state.vaccineKeyData.CHILD = CHILD;
      state.vaccineKeyData.ADULT = ADULT;
    },
    loadVaccineKeyApiData: (state, action: PayloadAction<VaccineKeyData>) => {
      const { INFANT, CHILD, ADULT } = action.payload;
      if (!state.vaccineKeyData) {
        state.vaccineKeyData = {
          INFANT: [],
          CHILD: [],
          ADULT: [],
        };
      }
      state.vaccineKeyData.INFANT = INFANT;
      state.vaccineKeyData.CHILD = CHILD;
      state.vaccineKeyData.ADULT = ADULT;
    },
    loadVaccineApiData: (state, action: PayloadAction<VaccineData>) => {
      const { INFANT, CHILD, ADULT } = action.payload;
      if (!state.vaccineData) {
        state.vaccineData = {
          INFANT: [],
          CHILD: [],
          ADULT: [],
        };
      }
      state.vaccineData.INFANT = INFANT;
      state.vaccineData.CHILD = CHILD;
      state.vaccineData.ADULT = ADULT;
    },
    // UI reducers for History Vaccine
    openVaccineHistoryModal: (
      state,
      action: PayloadAction<{
        doses: Dose[];
        schedule: VaccineSchedule | null;
        vaccine: VaccineBase;
        diseaseComplications: string;
        preventableDisease: string;
        diseaseCausedBy: string;
      }>
    ) => {
      if (!state.vaccineHistoryUI) return;
      state.vaccineHistoryUI.selectedVaccine = action.payload.vaccine;
      state.vaccineHistoryUI.selectedSchedule = action.payload.schedule;
      state.vaccineHistoryUI.showModalVaccinedOrders = true;
    },
    closeVaccineHistoryModal: state => {
      if (!state.vaccineHistoryUI) return;
      state.vaccineHistoryUI.showModalVaccinedOrders = false;
      state.vaccineHistoryUI.showAddForm = false;
      state.vaccineHistoryUI.vaccineModifying = null;
    },
    setVaccineHistorySelectedDoses: (
      state,
      action: PayloadAction<{
        doses: Dose[];
        schedule: VaccineSchedule | null;
        vaccine: VaccineBase;
        diseaseComplications: string;
        preventableDisease: string;
        diseaseCausedBy: string;
      }>
    ) => {
      if (!state.vaccineHistoryUI) return;
      state.vaccineHistoryUI.selectedVaccine = action.payload.vaccine;
    },
    setVaccineHistoryShowAddForm: (state, action: PayloadAction<boolean>) => {
      if (!state.vaccineHistoryUI) return;
      state.vaccineHistoryUI.showAddForm = action.payload;
    },
    setVaccineHistoryOrderRequest: (state, action: PayloadAction<OrderVaccineRequestObject>) => {
      if (!state.vaccineHistoryUI) return;
      state.vaccineHistoryUI.vaccineOrderRequest = action.payload;
    },
    resetVaccineHistoryOrderRequest: state => {
      if (!state.vaccineHistoryUI) return;
      state.vaccineHistoryUI.vaccineOrderRequest = defaultOrderVaccineRequestObject;
    },
    setVaccineHistoryModifying: (state, action: PayloadAction<VaccineOrderUpdateRequest | null>) => {
      if (!state.vaccineHistoryUI) return;
      state.vaccineHistoryUI.vaccineModifying = action.payload;
    },
  },
});

function insertOrdersIntoVaccineData(orders: OrderVaccine[], vaccineData: VaccineData): VaccineData {
  const ageGroups: (keyof VaccineData)[] = ['INFANT', 'CHILD', 'ADULT'];

  // Duyệt qua từng order
  for (const order of orders) {
    for (const group of ageGroups) {
      for (const schedule of vaccineData[group]) {
        // So sánh vaccine ID
        if (schedule.vaccine.id === order.vaccine.id) {
          for (const dose of schedule.doses) {
            if (dose.id === order.vaccineDose.id) {
              // Gán order info vào dose.order
              dose.order = order;
              break; // Dừng vòng lặp khi đã tìm thấy và gán order
            }
          }
        }
      }
    }
  }

  return vaccineData;
}

export const {
  loadVaccineData,
  loadVaccines,
  loadVaccineKeyData,
  loadVaccineApiData,
  loadVaccineKeyApiData,
  setInfoBookings,
  setInfoBookinguccessList,
  loadOrderVaccineToVaccineRender,
  loadVaccineOrdersGrouped,
  setReset,
  loadVaccineOrdersUserInfoMap,
  loadUserInfoVaccineOrderMap,
  openVaccineHistoryModal,
  closeVaccineHistoryModal,
  setVaccineHistorySelectedDoses,
  setVaccineHistoryShowAddForm,
  setVaccineHistoryOrderRequest,
  resetVaccineHistoryOrderRequest,
  setVaccineHistoryModifying,
  changeAgesRangeSelected,
} = vaccineSlice.actions;

export default vaccineSlice.reducer;
